import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    ChevronLeft,
    ChevronRight,
    Inbox,
    Users,
    UserPlus,
    UserMinus,
    X,
} from "lucide-react";
import {
    GetAllCompanyHR,
    AssignUserAssessment,
    GetAssignedUsersByAssessmentUID,
    UnassignUserAssessment
} from "../../../Services/SurveyService";
import SurveyNavTabs from "../../../../../Utility/SurveyNavTabs";
import { sanitizeHtml, stripHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../../../Utility/sanitizeHtml";
import SidebarHeaderImage from "../../../../../assests/SidebarHeaderImage.png";

const PAGE_SIZE = 10;

const initials = (name) =>
    name ? name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() : "";

const TableRowSkeleton = () => (
    <tr className="border-b border-gray-50 animate-pulse">
        <td className="py-3.5 pr-3">
            <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 shrink-0 rounded-full bg-gray-200" />
                <div className="h-4 w-28 rounded bg-gray-200" />
            </div>
        </td>
        <td className="py-3.5 pr-3"><div className="h-4 w-36 rounded bg-gray-200" /></td>
        <td className="py-3.5 pr-3"><div className="h-4 w-24 rounded bg-gray-200" /></td>
    </tr>
);

// A single selectable row inside the sidebar (Assign or Unassign tab)
const SidebarUserRow = ({ user, checked, onToggle }) => (
    <label
        className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
            checked ? "border-[#7f35b2] bg-[#c2a8f01a]" : "border-gray-100 hover:bg-gray-50"
        }`}
    >
        <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(user.hrUserUID)}
            className="h-4 w-4 shrink-0 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2]"
        />
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#c2a8f033] text-xs font-semibold text-[#7f35b2]">
            {initials(user.fullName)}
        </div>
        <div className="min-w-0">
            <p className="truncate text-sm font-medium text-gray-900">{user.fullName}</p>
            <p className="truncate text-xs text-gray-500">{user.email}</p>
        </div>
    </label>
);

const AllocateSurvey = () => {
    const { surveyUID: surveyUIDFromParams } = useParams();
    const location = useLocation();
    const {
        surveyUID = surveyUIDFromParams,
        surveyName,
        surveyDescription,
        viewOnly = false,
        surveyStatus,
    } = location.state || {};

    // Full company user pool (candidates for assignment)
    const [allUsers, setAllUsers] = useState([]);
    const [allUsersLoading, setAllUsersLoading] = useState(false);

    // Currently assigned users (drives the main table + Unassign tab)
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [assignedLoading, setAssignedLoading] = useState(false);

    // Main table search + pagination
    const [tableSearch, setTableSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    // Sidebar state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("assign"); // "assign" | "unassign"
    const [assignSearch, setAssignSearch] = useState("");
    const [unassignSearch, setUnassignSearch] = useState("");
    const [selectedAssignIds, setSelectedAssignIds] = useState(new Set());
    const [selectedUnassignIds, setSelectedUnassignIds] = useState(new Set());
    const [assigning, setAssigning] = useState(false);
    const [unassigning, setUnassigning] = useState(false);

    const alreadyAssignedIds = useMemo(
        () => new Set(assignedUsers.map((u) => u.hrUserUID)),
        [assignedUsers]
    );

    const fetchAllUsers = async () => {
        try {
            setAllUsersLoading(true);
            const response = await GetAllCompanyHR(surveyUID);
            if (response?.data?.status === "success") {
                setAllUsers(response?.data?.usersList || []);
            } else {
                CustomToast("fail", response?.data?.message || "Something went wrong please try again later");
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "An error occurred while fetching the Users");
        } finally {
            setAllUsersLoading(false);
        }
    };

    const fetchAssignedUsers = async () => {
        if (!surveyUID) return;
        try {
            setAssignedLoading(true);
            const response = await GetAssignedUsersByAssessmentUID(surveyUID);
            if (response?.data?.status === "success") {
                setAssignedUsers(response?.data?.response || []);
            } else {
                CustomToast("fail", response?.data?.message || "Unable to fetch assigned users.");
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "An error occurred while fetching assigned users.");
        } finally {
            setAssignedLoading(false);
        }
    };

    useEffect(() => {
        fetchAllUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchAssignedUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyUID]);

    // ----- Main table (assigned users) -----
    const filteredAssignedUsers = useMemo(() => {
        const term = tableSearch.toLowerCase();
        return assignedUsers.filter(
            (u) =>
                !term ||
                u.fullName?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term)
        );
    }, [assignedUsers, tableSearch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [tableSearch]);

    const totalPages = Math.max(1, Math.ceil(filteredAssignedUsers.length / PAGE_SIZE));
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const paginatedAssignedUsers = filteredAssignedUsers.slice(startIndex, startIndex + PAGE_SIZE);

    // ----- Sidebar: Assign tab candidates (not yet assigned) -----
    const assignCandidates = useMemo(() => {
        const term = assignSearch.toLowerCase();
        return allUsers.filter((u) => {
            if (alreadyAssignedIds.has(u.hrUserUID)) return false;
            if (!term) return true;
            return (
                u.fullName?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term)
            );
        });
    }, [allUsers, alreadyAssignedIds, assignSearch]);

    // ----- Sidebar: Unassign tab candidates (already assigned) -----
    const unassignCandidates = useMemo(() => {
        const term = unassignSearch.toLowerCase();
        return assignedUsers.filter((u) => {
            if (!term) return true;
            return (
                u.fullName?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term)
            );
        });
    }, [assignedUsers, unassignSearch]);

    const toggleAssignSelection = (id) => {
        setSelectedAssignIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleUnassignSelection = (id) => {
        setSelectedUnassignIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const openSidebar = (tab = "assign") => {
        setActiveTab(tab);
        setSidebarOpen(true);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
        setSelectedAssignIds(new Set());
        setSelectedUnassignIds(new Set());
        setAssignSearch("");
        setUnassignSearch("");
    };

    const handleAssignSelected = async () => {
        if (selectedAssignIds.size === 0) return;
        const idsToAssign = Array.from(selectedAssignIds);
        try {
            setAssigning(true);
            const response = await AssignUserAssessment({
                AssessmentUID: surveyUID,
                HRUserUIDs: idsToAssign.join(","),
            });
            if (response?.data?.status === "success") {
                CustomToast("success", response?.data?.message || "Assessment assigned successfully.");
                setSelectedAssignIds(new Set());
                await fetchAssignedUsers();
            } else {
                CustomToast("fail", response?.data?.message || "Unable to assign assessment.");
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "An error occurred while assigning the assessment.");
        } finally {
            setAssigning(false);
        }
    };

    const handleUnassignSelected = async () => {
        if (selectedUnassignIds.size === 0) return;
        const idsToUnassign = Array.from(selectedUnassignIds);
        try {
            setUnassigning(true);
            const response = await UnassignUserAssessment({
                AssessmentUID: surveyUID,
                HRUserUIDs: idsToUnassign.join(","),
            });
            if (response?.data?.status === "success") {
                CustomToast("success", response?.data?.message || "Users unassigned successfully.");
                setSelectedUnassignIds(new Set());
                await fetchAssignedUsers();
            } else {
                CustomToast("fail", response?.data?.message || "Unable to unassign users.");
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "An error occurred while unassigning users.");
        } finally {
            setUnassigning(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#f7f8fa]">
            <div className="mx-auto max-w-[1440px] py-2">
                <SurveyNavTabs
                    active="assign"
                    surveyUID={surveyUID}
                    surveyName={surveyName}
                    surveyDescription={surveyDescription}
                    viewOnly={viewOnly}
                    surveyStatus={surveyStatus}
                />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mx-auto max-w-[1440px] pb-6"
            >
                {surveyName && (
                    <div className="mb-4 rounded-2xl bg-gradient-to-br from-[#7f35b2]/10 to-[#7f35b2]/5 p-4">
                        <h2 className="text-base font-semibold text-gray-900">{surveyName}</h2>
                        {surveyDescription && (
                            <>
                                <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                                <div
                                    className="description-rich-content mt-1 text-xs text-gray-600 line-clamp-2"
                                    title={stripHtml(surveyDescription)}
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(surveyDescription) }}
                                />
                            </>
                        )}
                    </div>
                )}

                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Manage Users</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Users currently assigned to this assessment.
                        </p>
                    </div>
                    <button
                        onClick={() => openSidebar("assign")}
                        className="flex items-center gap-2 rounded-lg bg-[#7f35b2] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#6b2c96] transition-colors"
                    >
                        <UserPlus size={16} />
                        Assign User
                    </button>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-md">
                    {/* Search */}
                    <div className="mb-4">
                        <div className="relative min-w-[220px] max-w-sm">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={tableSearch}
                                onChange={(e) => setTableSearch(e.target.value)}
                                placeholder="Search by name or email..."
                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    <th className="py-3 pr-3">User</th>
                                    <th className="py-3 pr-3">Email</th>
                                    <th className="py-3 pr-3">Assigned Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {assignedLoading ? (
                                    Array.from({ length: 5 }).map((_, idx) => <TableRowSkeleton key={idx} />)
                                ) : paginatedAssignedUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" className="py-12 text-center text-gray-400">
                                            <div className="flex flex-col items-center justify-center gap-2">
                                                <Inbox size={32} className="text-gray-300" />
                                                <p className="text-sm font-medium">No assigned users found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedAssignedUsers.map((user) => (
                                        <tr
                                            key={user.hrUserUID}
                                            className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="py-3 pr-3">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#c2a8f033] text-xs font-semibold text-[#7f35b2]">
                                                        {initials(user.fullName)}
                                                    </div>
                                                    <span className="font-medium text-gray-900">{user.fullName}</span>
                                                </div>
                                            </td>
                                            <td className="py-3 pr-3 text-gray-500">{user.email}</td>
                                            <td className="py-3 pr-3 text-gray-500">{user.assignedDate}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer: pagination only */}
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                        <p className="text-gray-500">
                            {`Showing ${paginatedAssignedUsers.length > 0 ? startIndex + 1 : 0} to ${startIndex + paginatedAssignedUsers.length} of ${filteredAssignedUsers.length} users`}
                        </p>

                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <ChevronLeft size={15} />
                            </button>
                            <span className="px-2 text-sm text-gray-600">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <ChevronRight size={15} />
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* ---------------- Sidebar Drawer ---------------- */}
            <AnimatePresence>
                {sidebarOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeSidebar}
                            className="fixed inset-0 z-[9998] bg-black/30"
                        />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "tween", duration: 0.25 }}
                            className="fixed right-0 top-0 bottom-0 z-[9999] flex w-full max-w-[520px] flex-col overflow-hidden bg-white shadow-2xl"
                        >
                            {/* Header */}
                            <div className="relative overflow-hidden flex items-center justify-between px-6 py-5 border-b bg-green-50">

                                {/* Header background image */}
                                <div
                                    className="absolute inset-0 bg-no-repeat bg-cover bg-center pointer-events-none"
                                    style={{ backgroundImage: `url(${SidebarHeaderImage})` }}
                                />

                                {/* Dark overlay */}
                                <div className="absolute inset-0 bg-black/35 pointer-events-none" />

                                {/* Header content */}
                                <div className="relative z-10 flex items-center justify-between w-full">

                                    <div className="flex items-center gap-3">

                                        <div className="bg-[#7f35b2]/30 p-2.5 rounded-xl shrink-0">
                                            <Users
                                                className="text-[#ffffff]"
                                                size={22}
                                            />
                                        </div>

                                        <div>
                                            <h3 className="text-2xl font-semibold text-gray-100">
                                                Manage Assignment
                                            </h3>

                                            <p className="text-sm text-gray-100 mt-0.5">
                                                Assign or unassign users for this assessment
                                            </p>
                                        </div>

                                    </div>

                                    <button
                                        onClick={closeSidebar}
                                        className="p-2 rounded-lg hover:bg-gray-200/60 transition text-gray-100"
                                    >
                                        <X size={22} />
                                    </button>

                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="flex border-b border-gray-100 px-6">
                                <button
                                    onClick={() => setActiveTab("assign")}
                                    className={`flex items-center gap-1.5 border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                                        activeTab === "assign"
                                            ? "border-[#7f35b2] text-[#7f35b2]"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    <UserPlus size={15} />
                                    Assign
                                </button>
                                <button
                                    onClick={() => setActiveTab("unassign")}
                                    className={`flex items-center gap-1.5 border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                                        activeTab === "unassign"
                                            ? "border-[#7f35b2] text-[#7f35b2]"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    <UserMinus size={15} />
                                    Unassign
                                </button>
                            </div>

                            {/* Tab content */}
                            {activeTab === "assign" ? (
                                <>
                                    <div className="px-6 pt-4">
                                        <div className="relative">
                                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                value={assignSearch}
                                                onChange={(e) => setAssignSearch(e.target.value)}
                                                placeholder="Search by name or email..."
                                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-9 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex-1 space-y-2 overflow-y-auto px-6 py-4">
                                        {allUsersLoading ? (
                                            <p className="py-8 text-center text-sm text-gray-400">Loading users...</p>
                                        ) : assignCandidates.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center gap-2 py-12 text-gray-400">
                                                <Inbox size={28} className="text-gray-300" />
                                                <p className="text-sm font-medium">No users available to assign</p>
                                            </div>
                                        ) : (
                                            assignCandidates.map((user) => (
                                                <SidebarUserRow
                                                    key={user.hrUserUID}
                                                    user={user}
                                                    checked={selectedAssignIds.has(user.hrUserUID)}
                                                    onToggle={toggleAssignSelection}
                                                />
                                            ))
                                        )}
                                    </div>

                                    <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
                                        <button
                                            onClick={closeSidebar}
                                            className="rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleAssignSelected}
                                            disabled={selectedAssignIds.size === 0 || assigning}
                                            className="flex items-center gap-2 rounded-lg bg-[#7f35b2] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#6b2c96] disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                                        >
                                            <Users size={16} />
                                            {assigning ? "Assigning..." : `Assign${selectedAssignIds.size > 0 ? ` (${selectedAssignIds.size})` : ""}`}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="px-6 pt-4">
                                        <div className="relative">
                                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                value={unassignSearch}
                                                onChange={(e) => setUnassignSearch(e.target.value)}
                                                placeholder="Search by name or email..."
                                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-9 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex-1 space-y-2 overflow-y-auto px-6 py-4">
                                        {assignedLoading ? (
                                            <p className="py-8 text-center text-sm text-gray-400">Loading users...</p>
                                        ) : unassignCandidates.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center gap-2 py-12 text-gray-400">
                                                <Inbox size={28} className="text-gray-300" />
                                                <p className="text-sm font-medium">No assigned users found</p>
                                            </div>
                                        ) : (
                                            unassignCandidates.map((user) => (
                                                <SidebarUserRow
                                                    key={user.hrUserUID}
                                                    user={user}
                                                    checked={selectedUnassignIds.has(user.hrUserUID)}
                                                    onToggle={toggleUnassignSelection}
                                                />
                                            ))
                                        )}
                                    </div>

                                    <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
                                        <button
                                            onClick={closeSidebar}
                                            className="rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleUnassignSelected}
                                            disabled={selectedUnassignIds.size === 0 || unassigning}
                                            className="flex items-center gap-2 rounded-lg border border-red-500 bg-white px-5 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                                        >
                                            <UserMinus size={16} />
                                            {unassigning ? "Unassigning..." : `Unassign${selectedUnassignIds.size > 0 ? ` (${selectedUnassignIds.size})` : ""}`}
                                        </button>
                                    </div>
                                </>
                            )}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AllocateSurvey;